/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	Container,
	Flex,
	SimpleGrid,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	Tooltip,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { faDatabase, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BreadcrumbComponent from '../../../../components/BreadcrumbComponent/BreadcrumbComponent';
import CompanyName from '../../../../components/CompanyName.js';
import LabelDate from '../../../../components/LabelDate.js';
import SpreadSheetKeyValuePair from '../../../../components/MarketDevelopment/Miscellaneous/spreadsheet-key-value-pair.js';
import CompanyBadge from '../../../../components/Other/CompanyBadge.js';
import PrintLayout from '../../../../components/Print/PrintLayout';
import c from '../../../../constant';
import useStore from '../../../../store/index.js';
import { detailORDRatioCells } from '../../../../utils/ews-ord-utils.js';
import { detailSFPRatioCells } from '../../../../utils/ews-sfp-utils.js';
import { detailSPLOCIRatioCells } from '../../../../utils/ews-sploci-utils.js';
import { EWSTypeCurrency, EWSTypeTemplate } from '../../../../utils/ews.js';
import EWSORDRATIO from '../ews-ord-ratio.js';
import RiskMatrixTable from '../ews-risk-level.js';
import EWSSFPRATIO from '../ews-sfp-ratio.js';
import EWSSPLOCIRATIO from '../ews-sploci-ratio.js';
import EWSLifeSpreadSheetTable from './RatioGroup/Life/ews-life.js';
import RatioLifeCompanyRiskRankingSpreadSheetTable from './RatioGroup/Life/ratio-life-company-risk-ranking.js';
import RatioLifeGroupingRiskBasedSpreadSheetTable from './RatioGroup/Life/ratio-life-grouping-risk-based.js';
import RatioLifeGroupingRiskSpreadSheetTable from './RatioGroup/Life/ratio-life-grouping-risk.js';
import RatioLifeRequiredSolvencySpreadSheetTable from './RatioGroup/Life/ratio-life-required-solvency.js';
import EWSNonLifeSpreadSheetTable from './RatioGroup/NonLife/ews-nonlife.js';
import RatioNonLifeCompanyRiskRankingSpreadSheetTable from './RatioGroup/NonLife/ratio-nonLife-company-risk-ranking.js';
import RatioNonLifeGroupingRiskBasedSpreadSheetTable from './RatioGroup/NonLife/ratio-nonLife-grouping-risk-based.js';
import RatioNonLifeGroupingRiskSpreadSheetTable from './RatioGroup/NonLife/ratio-nonLife-grouping-risk.js';
import RatioNonLifeRequiredSolvencySpreadSheetTable from './RatioGroup/NonLife/ratio-nonLife-required-solvency.js';
import Loading from '../../../../components/Loading.js';
import LoadingOverlayText from '../../../../components/LoadingOverlay/LoadingOverlayText.js';

const Q_STATEMENTS_ALL_YEAR = gql`
	query getAnnuallyFinancialStatementRatioAllYear($statementId: String!) {
		getAnnuallyFinancialStatementRatioAllYear(statementId: $statementId) {
			id
			year
			createdAt
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			savedTemplatesCY
			savedTemplatesPY
			savedTemplatesP2Y
		}
	}
`;

const Q__GET_LATEST_STATEMENTS_ALL_YEAR = gql`
	query getLatestAnnuallyFinancialStatementRatioAllYear($statementId: String!) {
		getLatestAnnuallyFinancialStatementRatioAllYear(statementId: $statementId) {
			id
			year
			updatedAt
			company {
				id
				name
				type
			}
			savedTemplatesCY
			savedTemplatesPY
			savedTemplatesP2Y
		}
	}
`;

const Q_TEMPLATES = gql`
	query get($report: EarlyWarningTypeReport!) {
		getEarlyWarningTemplates(report: $report) {
			id
			titleKhmer
			titleEnglish
			code
			order
			labels {
				id
				code
				titleKhmer
				titleEnglish
				level
				order
				readOnly
			}
		}
	}
`;

const EWSRatioTemplate = {
	RATIO: 'RATIO',
	DATA: 'DATA',
};

function AnnualFinancialStatementRatioDetail() {
	const location = useLocation();
	const toast = useToast();
	const urlParams = new URLSearchParams(location.search);
	const year = urlParams.get('year') || null;
	const companyType = urlParams.get('companyType') || null;
	const companyId = urlParams.get('companyId') || null;
	const statementId = urlParams.get('statementId') || null;
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [tabIndex, setTabIndex] = useState(0);
	const [detailCY, setDetailCY] = useState(null);
	const [baseTemplateCY, setBaseTemplateCY] = useState(null);
	const [detailPY, setDetailPY] = useState(null);
	const [baseTemplatePY, setBaseTemplatePY] = useState(null);
	const [detailP2Y, setDetailP2Y] = useState(null);
	const [baseTemplateP2Y, setBaseTemplateP2Y] = useState(null);
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [showUpdateDataButton, setUpdateDataButton] = useState(false);
	const [objects, setObjects] = useState(null);
	const [nonLifeData, setNonLifeData] = useState(null);
	const [lifeData, setLifeData] = useState(null);
	const [groupingRiskData, setGroupingRiskData] = useState(null);
	const [requiredSolvencyData, setRequiredSolvency] = useState(null);
	const [groupingRiskBasedData, setGroupingRiskBasedData] = useState(null);
	const [companyRiskRankingData, setCompanyRiskRankingData] = useState(null);
	const [updatedRatio, setUpdatedRatio] = useState(0);
	const [fetchListAllYear, { data: dataGet, loading: loadingDataAllYears }] = useLazyQuery(Q_STATEMENTS_ALL_YEAR);
	const [fetchTemplates, { data: dataTemplate }] = useLazyQuery(Q_TEMPLATES);
	const [updateListAllYear, { data: dataUpdateList, loading: loadingUpdateList }] = useLazyQuery(Q__GET_LATEST_STATEMENTS_ALL_YEAR, {
		onCompleted: (res) => {
			const latest = res?.getLatestAnnuallyFinancialStatementRatioAllYear;

			setDetailCY(latest);
			setDetailPY(latest);
			setDetailP2Y(latest);

			setBaseTemplateCY(latest?.savedTemplatesCY || []);
			setBaseTemplatePY(latest?.savedTemplatesPY || []);
			setBaseTemplateP2Y(latest?.savedTemplatesP2Y || []);

			setUpdatedRatio((prev) => prev + 1);

			toast({
				title: t('Latest data updated successfully'),
				status: 'success',
				position: 'top-right',
			});

			// for update data button after user's input
			sessionStorage.setItem('recalculateNonLifeRatio', 'true');
			sessionStorage.setItem('recalculateLifeRatio', 'true');
			window.location.reload();
		},
		onError: () => {
			toast({
				title: t('Failed to update latest data'),
				status: 'error',
				position: 'top-right',
			});
		},
	});

	const calculatedRiskRanking = companyRiskRankingData?.q1Cells?.[0]?.[0]?.value;

	const riskRankingValue =
		calculatedRiskRanking !== undefined && calculatedRiskRanking !== null && calculatedRiskRanking !== ''
			? calculatedRiskRanking
			: detailCY?.companyRiskRanking;
	const hasAllDetails = baseTemplateCY?.length && baseTemplatePY?.length && baseTemplateP2Y?.length;

	useEffect(() => {
		if (dataTemplate?.getEarlyWarningTemplates && detailCY && detailPY && detailP2Y) {
			manipulateTemplates(dataTemplate.getEarlyWarningTemplates);
		}
	}, [detailCY, detailPY, detailP2Y]);

	useEffect(() => detailCY && fetchTemplates({ variables: { report: 'ews' } }), [detailCY]);

	useEffect(() => dataTemplate?.getEarlyWarningTemplates && manipulateTemplates(dataTemplate?.getEarlyWarningTemplates), [dataTemplate]);

	// Default Data

	useEffect(() => {
		if (statementId) {
			fetchListAllYear({ variables: { statementId }, fetchPolicy: 'network-only' });
		}
	}, [statementId]);

	useEffect(() => {
		if (dataGet?.getAnnuallyFinancialStatementRatioAllYear) {
			const [recCY, recPY, recP2Y] = dataGet?.getAnnuallyFinancialStatementRatioAllYear;
			setDetailCY(recCY);
			setDetailPY(recPY);
			setDetailP2Y(recP2Y);
			setBaseTemplateCY(recCY?.savedTemplatesCY || []);
			setBaseTemplatePY(recPY?.savedTemplatesPY || []);
			setBaseTemplateP2Y(recP2Y?.savedTemplatesP2Y || []);
		}
	}, [dataGet]);

	// Fetch Latest Data

	useEffect(() => {
		if (dataUpdateList?.getLatestAnnuallyFinancialStatementRatioAllYear) {
			const latest = dataUpdateList?.getLatestAnnuallyFinancialStatementRatioAllYear;

			setDetailCY(latest);
			setDetailPY(latest);
			setDetailP2Y(latest);
			setBaseTemplateCY(latest?.savedTemplatesCY || []);
			setBaseTemplatePY(latest?.savedTemplatesPY || []);
			setBaseTemplateP2Y(latest?.savedTemplatesP2Y || []);
		}
	}, [dataUpdateList]);

	const handleLatestData = () => {
		if (!statementId) return;
		updateListAllYear({ variables: { statementId } });
	};

	function cellChange({ code = null, cells = [] }) {
		setObjects(
			objects?.reduce((pre, cur, i) => {
				let localPre = pre;
				if (code === cur?.code) {
					localPre.push({ ...cur, cells });
				} else {
					localPre.push({ ...cur });
				}
				return localPre;
			}, []),
		);
	}

	function manipulateTemplates(templates) {
		if (!templates || !detailCY || !detailPY || !detailP2Y) {
			setObjects([]);
			return;
		}

		const normalize = (s) => (s || '').trim().toUpperCase();

		const buildMergedDetailLabels = (cyLabels = [], pyLabels = [], p2yLabels = [], cyYear, pyYear, p2yYear) => {
			const byCode = new Map();

			const indexByCode = (labels, yearTag, year) => {
				labels?.forEach((label) => {
					const code = normalize(label?.templateLabel?.code);
					if (!code) return;

					let codeLabel = byCode?.get(code);
					if (!codeLabel) {
						codeLabel = {
							baseLabel: label?.templateLabel ? { ...label } : { templateLabel: { code } },
							CY: null,
							PY: null,
							P2Y: null,
						};
						byCode?.set(code, codeLabel);
					}

					codeLabel[yearTag] = {
						...label,
						year: label?.year ?? year,
						values: (label?.values || []).map((v) => ({
							...v,
							year: v?.year ?? year,
						})),
					};

					if (!codeLabel?.baseLabel?.templateLabel && label?.templateLabel) codeLabel.baseLabel = { ...label };
				});
			};

			indexByCode(cyLabels, 'CY', cyYear);
			indexByCode(pyLabels, 'PY', pyYear);
			indexByCode(p2yLabels, 'P2Y', p2yYear);

			const merged = [];
			for (const [, slot] of byCode) {
				const labelCY = slot.CY;
				const labelPY = slot.PY;
				const labelP2Y = slot.P2Y;

				const getValue = (label, currency) => label?.values?.find((v) => v?.currency === currency)?.value ?? '0';

				const vals = [
					{ value: getValue(labelCY, EWSTypeCurrency.USD), currency: EWSTypeCurrency.USD, year: labelCY?.year },
					{ value: getValue(labelPY, EWSTypeCurrency.USD), currency: EWSTypeCurrency.USD, year: labelPY?.year },
					{ value: getValue(labelP2Y, EWSTypeCurrency.USD), currency: EWSTypeCurrency.USD, year: labelP2Y?.year },
				];

				merged.push({
					...slot.baseLabel,
					values: vals,
				});
			}

			return merged;
		};

		const ewsCY = baseTemplateCY?.length ? baseTemplateCY : (detailCY?.ews ?? []);
		const ewsPY = baseTemplatePY?.length ? baseTemplatePY : (detailPY?.ews ?? []);
		const ewsP2Y = baseTemplateP2Y?.length ? baseTemplateP2Y : (detailP2Y?.ews ?? []);

		const getEwsByCode = (arr, code) => arr.find((t) => normalize(t?.template?.code) === normalize(code));

		const localObjects = [];
		const templateCodes = [EWSTypeTemplate.SFP, EWSTypeTemplate.SPLOCI, EWSTypeTemplate.ORD];

		templateCodes?.forEach((wantedCodeRaw) => {
			const wantedCode = normalize(wantedCodeRaw);
			const def = templates.find((t) => normalize(t?.code) === wantedCode);
			if (!def) return;

			const labels = def?.labels ?? [];

			const detailCYLabels = getEwsByCode(ewsCY, wantedCode)?.labels ?? [];
			const detailPYLabels = getEwsByCode(ewsPY, wantedCode)?.labels ?? [];
			const detailP2YLabels = getEwsByCode(ewsP2Y, wantedCode)?.labels ?? [];

			const mergedDetailLabels = buildMergedDetailLabels(
				detailCYLabels,
				detailPYLabels,
				detailP2YLabels,
				detailCY?.year,
				detailPY?.year,
				detailP2Y?.year,
			);

			const readOnly = true;
			let cells = [];

			if (wantedCode === normalize(EWSTypeTemplate.SFP)) {
				cells = detailSFPRatioCells({ code: def.code, labels, detailLabels: mergedDetailLabels, readOnly });
			} else if (wantedCode === normalize(EWSTypeTemplate.SPLOCI)) {
				cells = detailSPLOCIRatioCells({ code: def.code, labels, detailLabels: mergedDetailLabels, readOnly });
			} else if (wantedCode === normalize(EWSTypeTemplate.ORD)) {
				cells = detailORDRatioCells({ code: def.code, labels, detailLabels: mergedDetailLabels, readOnly });
			}

			localObjects.push({ ...def, code: def.code, labels, cells });
		});

		setObjects(localObjects);
	}

	const handleNonLifeExport = (data) => {
		setNonLifeData(data);
	};

	const handleLifeExport = (data) => {
		setLifeData(data);
	};

	const handleRequiredSolvencyExport = (data) => {
		setRequiredSolvency(data);
	};

	const handleGroupingRiskExport = (data) => {
		setGroupingRiskData(data);
	};

	const handleGroupingRiskBasedExport = (data) => {
		setGroupingRiskBasedData(data);
	};

	const handleCompanyRiskRankingExport = (data) => {
		setCompanyRiskRankingData(data);
	};

	useEffect(() => {
		if (!user?.role) return;

		const roles = user.role.split(',').map((r) => r.trim());
		const ews = user.ews || [];

		let showUpdateButton = false;

		if (roles.includes('admin')) {
			showUpdateButton = true;
		} else if (roles.includes('ews')) {
			showUpdateButton =
				ews.includes('ewsCanViewStatementGeneralInsurance') ||
				ews.includes('ewsCanViewStatementLifeInsurance') ||
				ews.includes('ewsCanViewStatementMicroInsurance');
		}

		setUpdateDataButton(showUpdateButton);
	}, [user]);

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Early Warning System',
								path: '/early-warning-system/annual-financial-statement-ratio/list?page=1',
							},
							{
								name: 'Detail',
								path: '#',
							},
						]}
					/>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb={4}
						>
							<Flex
								className="hidden-print responsive-header-for-table"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="16px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t('Annual Key Risk Indicator')}
								</Text>
								<Button
									className="hidden-print"
									isLoading={false}
									isDisabled={false}
									onClick={() => window?.print()}
									leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
									width={['100%', '100%', 'auto']}
									colorScheme="gray"
									variant="solid"
									mt={[2, 2, 0]}
								>
									{t('Print')}
								</Button>
							</Flex>
							{loadingUpdateList && <LoadingOverlayText />}
							{loadingDataAllYears || !detailCY || !objects ? (
								<Loading />
							) : (
								<>
									{detailCY && objects && (
										<>
											<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4} p={4}>
												<Flex
													flex={1}
													direction="column"
													border="1px dotted #dbdbdb"
													borderRadius={c.borderRadius}
													pl={4}
													pr={4}
													pt={4}
													pb={4}
												>
													<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={detailCY?.company} />} />
													<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={detailCY?.company?.type} />} />
													<SpreadSheetKeyValuePair
														label="Year"
														value={<LabelDate input={detailCY?.year} inputFormat="Y" outputFormat="YYYY" />}
													/>
													<SpreadSheetKeyValuePair
														label="Submitted At"
														value={<LabelDate input={detailCY?.createdAt} outputFormat={c.dateTimeFormat} />}
													/>
												</Flex>
											</SimpleGrid>
											<Box p={4}>
												<Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
													<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
														<Tab>
															<Tooltip label={t(EWSRatioTemplate.RATIO.toLowerCase())}>
																<Text>{t('Ratio')}</Text>
															</Tooltip>
														</Tab>
														<Tab>
															<Tooltip label={t(EWSRatioTemplate.DATA.toLowerCase())}>
																<Text>{t('Data')}</Text>
															</Tooltip>
														</Tab>
													</TabList>
													<TabPanels>
														<TabPanel>
															<Flex pl={0} pr={0} pt={0} pb={0}>
																<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4}>
																	<Flex w="100%" direction="column" borderRadius={c.borderRadius}>
																		<Box
																			textAlign="center"
																			border="1.5px solid black"
																			backgroundColor="#a8d08d"
																			w="100%"
																			h="169px"
																			pt={2}
																			mb={5}
																		>
																			<Text mt={2} fontSize="18px" fontWeight="bold">
																				{t('Form EWS - Early Warning System')}
																			</Text>
																			<Text mt={2} fontSize="18px" fontWeight="bold">
																				{detailCY?.company?.name}
																			</Text>
																			<Text mt={2} fontSize="18px" fontWeight="bold">
																				FY{detailCY?.year}
																			</Text>
																			<Flex mt="10px">
																				<Box border="0.5px solid black" backgroundColor="#ffd965" w="50%">
																					<Text fontSize="18px" fontWeight="bold" p={2}>
																						{t('Risk Ranking')}:
																					</Text>
																				</Box>
																				<Box
																					border="0.5px solid black"
																					backgroundColor={
																						!hasAllDetails
																							? '#9f9f9fff'
																							: riskRankingValue <= 0
																								? '#5A8F3D'
																								: riskRankingValue <= 1
																									? '#B3B34A'
																									: riskRankingValue <= 2
																										? '#F4CF62'
																										: riskRankingValue <= 3
																											? '#F28E5E'
																											: riskRankingValue > 3
																												? '#E45A4F'
																												: ''
																					}
																					w="50%"
																				>
																					<Text fontSize="18px" fontWeight="bold" p={2}>
																						{hasAllDetails ? riskRankingValue : 'N/A'}
																					</Text>
																				</Box>
																			</Flex>
																		</Box>
																	</Flex>
																	<Flex>
																		<RiskMatrixTable />
																	</Flex>
																</SimpleGrid>
															</Flex>
															{(detailCY?.company?.type === 'GENERAL_INSURANCE' ||
																detailCY?.company?.type === 'MICRO_INSURANCE' ||
																detailCY?.company?.type === 'REINSURANCE') && (
																<>
																	<Box mt={6}>
																		<EWSNonLifeSpreadSheetTable
																			key={`non-life-${updatedRatio}`}
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			sfp={objects?.find((obj) => obj?.code === 'SFP')}
																			sploci={objects?.find((obj) => obj?.code === 'SPLOCI')}
																			ord={objects?.find((obj) => obj?.code === 'ORD')}
																			nonLifeDataExport={handleNonLifeExport}
																			updatedRatio={updatedRatio}
																			companyRiskRanking={calculatedRiskRanking}
																		/>
																	</Box>
																	<Box mt={8}>
																		<RatioNonLifeRequiredSolvencySpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			companyRiskRankingData={companyRiskRankingData}
																			requiredSolvencyExport={handleRequiredSolvencyExport}
																		/>
																	</Box>
																	<Box mt="8">
																		<RatioNonLifeGroupingRiskSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			nonLifeData={nonLifeData}
																			sfp={objects?.find((obj) => obj?.code === 'SFP')}
																			groupingRiskDataExport={handleGroupingRiskExport}
																		/>
																	</Box>
																	<Box mt="4">
																		<RatioNonLifeGroupingRiskBasedSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			groupingRiskData={groupingRiskData}
																			nonLifeData={nonLifeData}
																			sfp={objects?.find((obj) => obj?.code === 'SFP')}
																			requiredSolvencyData={requiredSolvencyData}
																			groupingRiskBasedExport={handleGroupingRiskBasedExport}
																		/>
																	</Box>
																	<Box mt="4">
																		<RatioNonLifeCompanyRiskRankingSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			groupingRiskBasedData={groupingRiskBasedData}
																			companyRiskRankingDataExport={handleCompanyRiskRankingExport}
																		/>
																	</Box>
																</>
															)}
															{detailCY?.company?.type === 'LIFE_INSURANCE' && (
																<>
																	<Box mt={6}>
																		<EWSLifeSpreadSheetTable
																			key={`life-${updatedRatio}`}
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			sfp={objects?.find((obj) => obj?.code === 'SFP')}
																			sploci={objects?.find((obj) => obj?.code === 'SPLOCI')}
																			ord={objects?.find((obj) => obj?.code === 'ORD')}
																			lifeDataExport={handleLifeExport}
																			updatedRatio={updatedRatio}
																			companyRiskRanking={calculatedRiskRanking}
																		/>
																	</Box>
																	<Box mt={8}>
																		<RatioLifeRequiredSolvencySpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			companyRiskRankingData={companyRiskRankingData}
																			requiredSolvencyExport={handleRequiredSolvencyExport}
																		/>
																	</Box>
																	<Box mt="8">
																		<RatioLifeGroupingRiskSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			lifeData={lifeData}
																			groupingRiskDataExport={handleGroupingRiskExport}
																		/>
																	</Box>
																	<Box mt="4">
																		<RatioLifeGroupingRiskBasedSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			groupingRiskData={groupingRiskData}
																			requiredSolvencyData={requiredSolvencyData}
																			lifeData={lifeData}
																			sfp={objects?.find((obj) => obj?.code === 'SFP')}
																			groupingRiskBasedExport={handleGroupingRiskBasedExport}
																		/>
																	</Box>
																	<Box mt="4">
																		<RatioLifeCompanyRiskRankingSpreadSheetTable
																			year={year}
																			companyType={companyType}
																			companyId={companyId}
																			groupingRiskBasedData={groupingRiskBasedData}
																			companyRiskRankingDataExport={handleCompanyRiskRankingExport}
																		/>
																	</Box>
																</>
															)}
														</TabPanel>
														<TabPanel pl={0} pr={0} pb={0}>
															{showUpdateDataButton && (
																<Flex justify="flex-end">
																	<Button
																		variant="solid"
																		size="md"
																		h="40px"
																		w={lang === 'en' ? '128px' : '180px'}
																		mb={4}
																		bg="green"
																		_hover={{ bg: 'darkgreen' }}
																		color="white"
																		rightIcon={<FontAwesomeIcon icon={faDatabase} style={{ fontSize: 20 }} />}
																		onClick={onOpen}
																		isLoading={loadingUpdateList}
																		spinnerPlacement="start"
																	>
																		{t('Update Data')}
																	</Button>
																</Flex>
															)}
															{(() => {
																const sfp = objects?.find((el) => el?.code === EWSTypeTemplate.SFP);
																if (!sfp) return null;
																return (
																	<Box>
																		<EWSSFPRATIO
																			code={EWSTypeTemplate.SFP}
																			labels={sfp?.labels}
																			cells={sfp?.cells}
																			readOnly={true}
																			onChange={(result) => cellChange(result)}
																		/>
																	</Box>
																);
															})()}
															<Box my={10} />
															{(() => {
																const sploci = objects?.find((el) => el?.code === EWSTypeTemplate.SPLOCI);
																if (!sploci) return null;
																return (
																	<Box>
																		<EWSSPLOCIRATIO
																			code={EWSTypeTemplate.SPLOCI}
																			labels={sploci?.labels}
																			cells={sploci?.cells}
																			readOnly={true}
																			onChange={(result) => cellChange(result)}
																		/>
																	</Box>
																);
															})()}
															<Box my={10} />
															{(() => {
																const ord = objects?.find((el) => el?.code === EWSTypeTemplate.ORD);
																if (!ord) return null;
																return (
																	<Box>
																		<EWSORDRATIO
																			code={EWSTypeTemplate.ORD}
																			labels={ord?.labels}
																			cells={ord?.cells}
																			readOnly={true}
																			onChange={(result) => cellChange(result)}
																		/>
																	</Box>
																);
															})()}
														</TabPanel>
													</TabPanels>
												</Tabs>
											</Box>
										</>
									)}
								</>
							)}
						</Box>
					</Center>
				</Container>
				<AlertDialog isOpen={isOpen} leastDestructiveRef={undefined} onClose={onClose}>
					<AlertDialogOverlay />
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Confirmation')}
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="16px">{t('Are you sure you want to update to the latest data?')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button onClick={onClose}>{t('Cancel')}</Button>
							<Button
								colorScheme="green"
								ml={3}
								onClick={() => {
									onClose();
									handleLatestData();
								}}
							>
								{t('Update')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</>
		</PrintLayout>
	);
}

export default AnnualFinancialStatementRatioDetail;
